import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { avatars } from '@/config/siteImages';

const testimonialKeys = [
  {
    nameKey: 'testimonial_1_name',
    businessKey: 'testimonial_1_business',
    amountKey: 'testimonial_1_amount',
    quoteKey: 'testimonial_1_quote',
    disclaimerKey: 'testimonial_1_disclaimer',
    avatar: avatars.small.testimonial1,
  },
  {
    nameKey: 'testimonial_2_name',
    businessKey: 'testimonial_2_business',
    amountKey: 'testimonial_2_amount',
    quoteKey: 'testimonial_2_quote',
    disclaimerKey: 'testimonial_2_disclaimer',
    avatar: avatars.small.testimonial2,
  },
  {
    nameKey: 'testimonial_3_name',
    businessKey: 'testimonial_3_business',
    amountKey: 'testimonial_3_amount',
    quoteKey: 'testimonial_3_quote',
    disclaimerKey: 'testimonial_3_disclaimer',
    avatar: avatars.small.testimonial3,
  },
  {
    nameKey: 'testimonial_4_name',
    businessKey: 'testimonial_4_business',
    amountKey: 'testimonial_4_amount',
    quoteKey: 'testimonial_4_quote',
    disclaimerKey: 'testimonial_4_disclaimer',
    avatar: avatars.small.testimonial4,
  },
  {
    nameKey: 'testimonial_5_name',
    businessKey: 'testimonial_5_business',
    amountKey: 'testimonial_5_amount',
    quoteKey: 'testimonial_5_quote',
    disclaimerKey: 'testimonial_5_disclaimer',
    avatar: avatars.small.testimonial5,
  },
];

export default function Testimonials() {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState(0);

  const prev = () => setActiveIdx((i) => (i - 1 + testimonialKeys.length) % testimonialKeys.length);
  const next = () => setActiveIdx((i) => (i + 1) % testimonialKeys.length);

  return (
    <section className="py-24 bg-white px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">
            {t('testimonials_eyebrow') || 'Trusted by 2,400+ Business Owners'}
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
            {t('testimonials_title')}
          </h2>
          <p className="text-sm text-gray-500 max-w-xl mx-auto leading-relaxed">
            {t('testimonials_subtitle')}
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden lg:grid grid-cols-3 gap-5">
          {testimonialKeys.slice(0, 3).map(({ nameKey, businessKey, amountKey, quoteKey, disclaimerKey, avatar }) => (
            <div key={nameKey} className="bg-[#FCFAFD] border border-[#E9D9F2] rounded-2xl p-6 flex flex-col">
              <div className="flex items-center gap-0.5 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                ))}
              </div>
              <p className="text-[#4F2D63] text-base font-bold mb-3">{t(amountKey)}</p>
              <blockquote className="text-gray-600 text-sm leading-relaxed italic flex-1 mb-5">
                &ldquo;{t(quoteKey)}&rdquo;
              </blockquote>
              <div className="flex items-center gap-3 border-t border-[#E9D9F2] pt-4">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img src={avatar} alt={t(nameKey)} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t(nameKey)}</p>
                  <p className="text-xs text-gray-400">{t(businessKey)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 italic mt-3">{t(disclaimerKey)}</p>
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="lg:hidden">
          <div className="bg-[#FCFAFD] border border-[#E9D9F2] rounded-2xl p-6">
            <div className="flex items-center gap-0.5 mb-4">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
              ))}
            </div>
            <p className="text-[#4F2D63] text-base font-bold mb-3">{t(testimonialKeys[activeIdx].amountKey)}</p>
            <blockquote className="text-gray-600 text-sm leading-relaxed italic mb-5">
              &ldquo;{t(testimonialKeys[activeIdx].quoteKey)}&rdquo;
            </blockquote>
            <div className="flex items-center gap-3 border-t border-[#E9D9F2] pt-4">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                <img src={testimonialKeys[activeIdx].avatar} alt={t(testimonialKeys[activeIdx].nameKey)} className="w-full h-full object-cover object-top" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{t(testimonialKeys[activeIdx].nameKey)}</p>
                <p className="text-xs text-gray-400">{t(testimonialKeys[activeIdx].businessKey)}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-5">
            <button onClick={prev} className="w-9 h-9 flex items-center justify-center rounded-full border border-[#E9D9F2] text-gray-500 hover:border-[#4F2D63] hover:text-[#4F2D63] cursor-pointer transition-colors">
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <div className="flex gap-1.5">
              {testimonialKeys.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  className={`h-1.5 rounded-full transition-all cursor-pointer ${i === activeIdx ? 'bg-[#4F2D63] w-5' : 'bg-gray-200 w-1.5'}`}
                />
              ))}
            </div>
            <button onClick={next} className="w-9 h-9 flex items-center justify-center rounded-full border border-[#E9D9F2] text-gray-500 hover:border-[#4F2D63] hover:text-[#4F2D63] cursor-pointer transition-colors">
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Desktop extra 2 testimonials */}
        <div className="hidden lg:grid grid-cols-2 gap-5 mt-5">
          {testimonialKeys.slice(3).map(({ nameKey, businessKey, amountKey, quoteKey, disclaimerKey, avatar }) => (
            <div key={nameKey} className="bg-[#F8F3FB] border border-[#E9D9F2] rounded-2xl p-6 flex gap-4">
              <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 mt-0.5">
                <img src={avatar} alt={t(nameKey)} className="w-full h-full object-cover object-top" />
              </div>
              <div>
                <div className="flex items-center gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                  ))}
                </div>
                <p className="text-[#4F2D63] text-sm font-bold mb-1.5">{t(amountKey)}</p>
                <blockquote className="text-gray-600 text-sm leading-relaxed italic mb-2.5">
                  &ldquo;{t(quoteKey)}&rdquo;
                </blockquote>
                <p className="text-gray-800 font-semibold text-xs">{t(nameKey)}</p>
                <p className="text-[#0F766E] text-xs">{t(businessKey)}</p>
                <p className="text-gray-400 text-xs italic mt-1">{t(disclaimerKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
